export interface User {
  id: number;
  email: string;
  name?: string | null;
  bio?: string | null;
  avatarImage?: string | null;
  avatarEmoji?: string | null;
  newStatusAnimation?: NewStatusAnimation;
  locale?: Locale | null;
  createdAt?: string;
  updatedAt?: string;
}

export type Locale = 'en' | 'uk' | 'es' | 'fr' | 'no' | 'ro' | 'nl';

export enum NewStatusAnimation {
  FIVE_SECONDS = 'five_seconds',
  TEN_SECONDS = 'ten_seconds',
  NEVER = 'never',
  UNTIL_OPENED = 'until_opened',
}

// Public endpoints deliberately omit private account fields such as email.
export type PublicUser = Pick<
  User,
  'id' | 'name' | 'bio' | 'avatarImage' | 'avatarEmoji'
>;

export type FavoriteUser = PublicUser & {
  location?: { latitude?: number; longitude?: number; updatedAt: string };
};

export interface FavoritesResponse {
  users: FavoriteUser[];
  statuses: UserStatusWithUser[];
}

export interface IgnoredUsersResponse {
  users: PublicUser[];
}

export enum LocationType {
  BROWSER = 'browser',
  MANUAL = 'manual',
}

export interface UserCoordinates {
  id: number;
  userId: number;
  latitude: number;
  longitude: number;
  updatedAt: string;
  user: PublicUser;
  type?: LocationType;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
  bio?: string;
}

export interface RegistrationResponse extends User {
  registrationCountryCode: string | null;
  registrationCountryRank: number;
}

export interface RegistrationCelebration {
  countryCode: string | null;
  countryRank: number;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

export interface ApiKeyMetadata {
  exists: boolean;
  prefix?: string;
  createdAt?: string;
  lastUsedAt?: string | null;
}

export interface GeneratedApiKey extends ApiKeyMetadata {
  token: string;
}

export interface AuthResponse extends TokenResponse {
  user: User;
}

export interface UpdateProfileRequest {
  name?: string;
  bio?: string;
  avatarImage?: string;
  avatarEmoji?: string;
  newStatusAnimation?: NewStatusAnimation;
  locale?: Locale;
}

export interface UpdateCoordinatesRequest {
  latitude: number;
  longitude: number;
  type?: LocationType;
}

export interface ApiError {
  message: string | string[];
  statusCode: number;
}

// User Status Types
export enum UserStatusType {
  MESSAGE = 'message',
  EVENT = 'event',
  ANNOUNCEMENT = 'announcement',
  PARTY = 'party',
}

export interface UserStatus {
  id: number;
  userId: number;
  type: UserStatusType;
  message: string;
  link?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  createdAt: string;
  updatedAt: string;
  likesCount: number;
  commentsCount?: number;
  isLikedByCurrentUser: boolean;
  isFavoriteByCurrentUser?: boolean;
}

export interface UserStatusWithUser extends UserStatus {
  user: PublicUser;
}

export interface PublicPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface PublicPlaceSummary {
  country: string;
  city: string;
  storyCount: number;
  updatedAt: string;
}

export type HashtagEntityType = 'status' | 'status_reply' | 'direct_message';

export interface HashtagSummary {
  name: string;
  displayName: string;
  usageCount: number;
  entityCount: number;
  country?: string;
  city?: string;
}

export interface HashtagPage {
  items: HashtagSummary[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface HashtagEntityResult {
  entityType: HashtagEntityType;
  entityId: number;
  contextId: number;
  authorId: number;
  occurrenceCount: number;
  text: string;
  country?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
}

export interface HashtagEntityPage {
  hashtag: string;
  items: HashtagEntityResult[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface HashtagSearchQuery {
  search?: string;
  country?: string;
  city?: string;
  limit?: number;
  offset?: number;
}

export interface HashtagEntitySearchQuery {
  entityType?: HashtagEntityType;
  country?: string;
  city?: string;
  limit?: number;
  offset?: number;
}

export interface CreateUserStatusRequest {
  type: UserStatusType;
  message: string;
  link?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
}

export interface StatusComment {
  id: number;
  message: string;
  createdAt: string;
  updatedAt: string;
  editedAt: string | null;
  likesCount: number;
  isLikedByCurrentUser: boolean;
  user: PublicUser;
}

export interface NewCommentEvent {
  statusId: number;
  commentsCount: number;
  comment: StatusComment;
}

export interface UpdatedCommentEvent {
  statusId: number;
  comment: StatusComment;
}

export interface StatusLikeUpdateEvent {
  statusId: number;
  userId: number;
  likesCount: number;
  isLiked: boolean;
}

export interface CommentLikeUpdateEvent extends StatusLikeUpdateEvent {
  commentId: number;
}

export interface CommentPage {
  items: StatusComment[];
  nextCursor: string | null;
  hasMore: boolean;
}

export enum NotificationType {
  STATUS_REPLY = 'status_reply',
  DIRECT_MESSAGE = 'direct_message',
}

export interface AppNotification {
  id: number;
  type: NotificationType;
  statusId: number | null;
  conversationId?: number | null;
  commentId: number;
  commentMessage: string;
  actor: PublicUser;
  readAt: string | null;
  createdAt: string;
}

export interface Conversation {
  id: number;
  otherUser: PublicUser;
  lastMessage: StatusComment | null;
  unreadCount: number;
  updatedAt: string;
}

export interface ConversationPage {
  items: Conversation[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface DirectMessageSearchResult {
  conversationId: number;
  otherUser: PublicUser;
  message: StatusComment;
}

export interface DirectMessageSearchPage {
  items: DirectMessageSearchResult[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface DirectMessageEvent {
  conversationId: number;
  message: StatusComment;
}

export interface NotificationPage {
  items: AppNotification[];
  nextCursor: string | null;
  hasMore: boolean;
  unreadCount: number;
}

export interface GetUserStatusesQuery {
  limit?: number;
  type?: UserStatusType;
  from?: string;
  to?: string;
  squareHeight?: number;
  latitude?: number;
  longitude?: number;
}

export interface GetStatusesByLocationQuery {
  limit?: number;
  type?: UserStatusType;
  from?: string;
  to?: string;
  squareHeight: number;
  latitude?: number;
  longitude?: number;
}

export interface StatsResponseDto {
  usersCount: number;
  coordinatesCount: number;
  statusesCount: number;
  lastDayNewUsers: number;
  lastDayNewCoordinates: number;
  lastHourNewUsers: number;
  lastHourNewCoordinates: number;
  lastDayNewStatuses: number;
  lastHourNewStatuses: number;
  version: string;
}

export type ObserverSortDirection = 'asc' | 'desc';

export interface ObserverPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ObserverUser {
  id: number;
  email: string;
  name?: string | null;
  bio?: string | null;
  avatarImage?: string | null;
  avatarEmoji?: string | null;
  newStatusAnimation: string;
  locale?: string | null;
  registrationCountryCode?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  bannedAt?: string | null;
  banReason?: string | null;
}

export interface ObserverStatus {
  id: number;
  userId: number;
  type: string;
  message: string;
  link?: string | null;
  likesCount: number;
  commentsCount: number;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  country?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  bannedAt?: string | null;
  banReason?: string | null;
}

export interface ObserverLocation {
  id: number;
  userId: number;
  latitude: number | null;
  longitude: number | null;
  type: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface ObserverHashtag {
  id: number;
  normalized: string;
  displayName: string;
  usageCount: number;
  entityCount: number;
  mentionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ObserverHashtagBreakdown {
  entityType: string;
  country: string;
  city: string;
  usageCount: number;
  entityCount: number;
}

export interface ObserverHashtagDetail extends ObserverHashtag {
  breakdown: ObserverHashtagBreakdown[];
}

export interface ObserverUserHashtagMention {
  id: number;
  hashtagId: number;
  displayName: string;
  normalized: string;
  entityType: string;
  entityId: number;
  occurrenceCount: number;
  country: string;
  city: string;
  createdAt: string;
}

export interface ObserverUserReply {
  id: number;
  userId: number;
  statusId: number;
  message: string;
  likesCount: number;
  createdAt: string;
  updatedAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
}

export interface ObserverReply extends ObserverUserReply {
  bannedAt?: string | null;
  banReason?: string | null;
}

export interface ObserverStatistics {
  generatedAt: string;
  from?: string;
  to?: string;
  totals: Record<string, number>;
  period: Record<string, number>;
  breakdowns: {
    moderation?: Record<string, number>;
    statusesByType?: Record<string, number>;
    locationsByType?: Record<string, number>;
    hashtagMentionsByEntityType?: Record<string, number>;
    usersByLocale?: Record<string, number>;
    usersByRegistrationCountry?: Record<string, number>;
    reactionsByTargetType?: Record<string, number>;
    notificationsByType?: Record<string, number>;
    relationshipStates?: Record<string, number>;
    [key: string]: unknown;
  };
}

export interface ObserverListQuery {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: ObserverSortDirection;
  [key: string]: string | number | boolean | undefined;
}

// Frontend constants to avoid magic numbers
export const UI_ICON_SIZE = 30;
export const UI_ICON_ANCHOR = 15;
export const EMOJI_FONT_SIZE = 24;
export const WELCOME_MESSAGE_TIMEOUT_MS = 5000;
export const GEOLOCATION_TIMEOUT_MS = 10000;
export const GEOLOCATION_MAX_AGE_MS = 30000;
export const LOCATION_RETRY_ATTEMPTS = 3;
export const LOCATION_RETRY_DELAY_MS = 2000;
export const LOCATION_INIT_DELAY_MS = 1000;
export const LOCATION_ERROR_DISPLAY_TIMEOUT_MS = 15000;
export const EMOJI_MULTIPLIER = 7;
export const MAX_USERS_LIMIT = 1000;
export const FLY_TO_ZOOM = 15;
export const POPUP_MAX_WIDTH = 320;
export const TIME_CONVERSION_MS = 1000;
export const TIME_CONVERSION_MINUTES = 60;
export const TIME_CONVERSION_HOURS = 60;
export const TIME_CONVERSION_DAYS = 24;
export const TIME_CONVERSION_WEEK_DAYS = 7;
export const SKELETON_COUNT = 3;
